import React, {
  createContext, useContext, useCallback, useMemo
} from 'react';
import PropTypes from 'prop-types';
import {
  useFetchSweepstakes,
  useUpdateSweepstakes,
  useCompleteSweepstakesVideoStep,
} from '../hooks/fetch-sweepstakes';
import { isAdmin, isPurchaser } from '../common/utils/CardUtils';
import { isAuthenticated } from '../common/utils/AccountUtils';
import { LoyaltySweepstakesModel } from '../common/models/UserInfoModels';

const SweepstakesDataContext = createContext();

export const useSweepstakesData = () => {
  const context = useContext(SweepstakesDataContext);
  if (!context) {
    throw new Error('useSweepstakesData must be used within a SweepstakesDataProvider');
  }
  return context;
};

export const SweepstakesDataProvider = ({
  children, sweepstakesData: providedData, userOptInStatus, useRestAPI,
  forceAcceptSuccess, forceUnauthenticated, forcePurchaser
}) => {
  const effectivePurchaser = forcePurchaser || isPurchaser();

  // Fetch from API only if authenticated and not a purchaser
  const {
    challengeData, userProfileData, isLoading, hasError, isChallengeEnded, refresh
  } = useFetchSweepstakes({
    providedData,
    userOptInStatus,
    skip: !isAuthenticated() || effectivePurchaser,
    useRestAPI
  });
  // Use the update hook for managing runtime data changes
  const { effectiveData, updateSweepstakesData } = useUpdateSweepstakes(challengeData);
  const {
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
    completeVideoError,
  } = useCompleteSweepstakesVideoStep();

  // Update effective data manually during opt-in flow
  const handleOptIn = useCallback(() => {
    updateSweepstakesData((prev) => ({
      ...prev,
      isOptedIn: true,
    }));
  }, [updateSweepstakesData]);

  const contextValue = useMemo(() => ({
    sweepstakesData: effectiveData,
    userProfileData,
    isLoading,
    hasError,
    isChallengeEnded,
    refresh,
    updateSweepstakesData,
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
    completeVideoError,
    forceAcceptSuccess,
    forceUnauthenticated,
    handleOptIn,
    isAdmin: forcePurchaser ? false : isAdmin(),
    isPurchaser: effectivePurchaser,
  }), [
    effectiveData,
    userProfileData,
    isLoading,
    hasError,
    isChallengeEnded,
    refresh,
    updateSweepstakesData,
    completeSweepstakesVideoStep,
    isLoadingCompleteVideo,
    completeVideoError,
    forceAcceptSuccess,
    forceUnauthenticated,
    forcePurchaser,
    handleOptIn,
  ]);

  return (
    <SweepstakesDataContext.Provider value={contextValue}>
      {children}
    </SweepstakesDataContext.Provider>
  );
};

SweepstakesDataProvider.displayName = 'Sweepstakes Data Provider';
SweepstakesDataProvider.dataModel = LoyaltySweepstakesModel;

SweepstakesDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
  forceAcceptSuccess: PropTypes.bool,
  forcePurchaser: PropTypes.bool,
  forceUnauthenticated: PropTypes.bool,
  sweepstakesData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    logo: PropTypes.string,
    status: PropTypes.string,
    expirationDate: PropTypes.string,
    isOptedIn: PropTypes.bool,
    completedSteps: PropTypes.number,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
  }),
  userOptInStatus: PropTypes.oneOf(['notOptedIn', 'partiallyCompleted', 'fullyCompleted']),
};

SweepstakesDataProvider.defaultProps = {
  sweepstakesData: null,
  userOptInStatus: 'notOptedIn',
  forceAcceptSuccess: false,
  forcePurchaser: false,
  forceUnauthenticated: false,
};
